import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabase';

export interface SheetProduct {
  id: string;
  name: string;
  unit_price: number;
  category_id: string | null;
  is_available: boolean;
}

interface Props {
  storeId: string;
  orderId: string;
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
}

async function callBillEdit(payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('tapnpay-bill-edit', {
    body: payload,
  });
  if (error) throw error;
  return data;
}

export default function AddItemSheet({ storeId, orderId, open, onClose, onAdded }: Props) {
  const [products, setProducts] = useState<SheetProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('products')
        .select('id, name, price, unit_price, category_id, is_available')
        .eq('store_id', storeId)
        .order('name');
      if (cancelled) return;
      const rows = (data ?? []).map((p) => {
        const pp = p as Record<string, unknown>;
        return {
          id: pp.id as string,
          name: (pp.name as string) ?? '',
          unit_price: (pp.unit_price as number) ?? (pp.price as number) ?? 0,
          category_id: (pp.category_id as string | null) ?? null,
          is_available: pp.is_available !== false,
        } satisfies SheetProduct;
      });
      setProducts(rows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, storeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, query]);

  const handleAdd = async (p: SheetProduct) => {
    setBusy(p.id);
    try {
      await callBillEdit({
        action: 'add_item',
        order_id: orderId,
        payload: {
          product_id: p.id,
          quantity: 1,
          unit_price: p.unit_price,
        },
      });
      onAdded?.();
      onClose();
    } catch {
      // ignore; user can retry
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/40" onClick={onClose}>
      <div className="flex-1" />
      <div
        className="bg-surface max-h-[80vh] flex flex-col"
        style={{ borderTop: '0.5px solid #000' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 h-12"
          style={{ borderBottom: '0.5px solid #e5e7eb' }}
        >
          <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
            Ajouter un article
          </p>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[40px] min-w-[40px] flex items-center justify-center text-on-surface-variant"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
        <div className="px-4 py-3" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un produit"
            className="w-full min-h-[44px] px-3 text-sm text-on-surface bg-surface"
            style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-on-surface-variant">…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-on-surface-variant">
              Aucun produit
            </div>
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={busy === p.id || !p.is_available}
                onClick={() => void handleAdd(p)}
                className="w-full flex items-center justify-between px-4 py-3 min-h-[56px] text-left disabled:opacity-50"
                style={{ borderBottom: '0.5px solid #e5e7eb' }}
              >
                <div>
                  <p className="text-sm text-on-surface">{p.name}</p>
                  {!p.is_available && (
                    <p className="text-[10px] uppercase tracking-wider text-error">Rupture</p>
                  )}
                </div>
                <span className="text-sm tabular-nums text-on-surface-strong">
                  {p.unit_price.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
